<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Compact Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-3'
      ]"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <!-- Trophy Icon -->
          <div
            class="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500"
          >
            <svg
              class="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
          </div>
          <div>
            <h1
              :class="[theme.isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-xl font-bold']"
            >
              Quality Gates Compliance
            </h1>
            <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs']">
              Mandatory compliance tracking: all teams are expected to reach 100% completion
            </p>
          </div>
        </div>
        <button
          :disabled="loading"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-white hover:bg-gray-50 text-gray-700',
            'px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-medium transition-all flex items-center shadow-sm hover:shadow',
            loading ? 'opacity-50 cursor-not-allowed' : ''
          ]"
          @click="fetchLeaderboard"
        >
          <svg
            :class="[loading ? 'animate-spin' : '', 'w-3.5 h-3.5 mr-1.5']"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6 space-y-6">
      <!-- Compact Filters -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-3 shadow-sm'
        ]"
      >
        <div class="flex flex-wrap items-center gap-4">
          <!-- Team Filter (Always shown, with loading state) -->
          <div class="flex items-center space-x-2">
            <label
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium whitespace-nowrap'
              ]"
            >
              Team:
            </label>
            <div class="relative">
              <select
                v-model="filters.team"
                :disabled="loadingTeams"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                  'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer pr-8',
                  loadingTeams ? 'opacity-75 cursor-wait' : ''
                ]"
                @change="fetchLeaderboard"
              >
                <option value="">All Teams</option>
                <option
                  v-for="team in teams"
                  :key="team"
                  :value="team"
                >
                  {{ leaderboardService.formatTeamName(team) }}
                </option>
              </select>
              <!-- Loading Spinner -->
              <div
                v-if="loadingTeams"
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                <svg
                  class="animate-spin h-3 w-3 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Divider -->
          <div :class="[theme.isDark.value ? 'bg-gray-700' : 'bg-gray-300', 'w-px h-6']"></div>

          <!-- Time Range -->
          <div class="flex items-center space-x-2">
            <label
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium whitespace-nowrap'
              ]"
            >
              Time Range:
            </label>
            <select
              v-model="filters.timeRange"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="fetchLeaderboard"
            >
              <option value="7d">Last 7 Days</option>
              <option value="14d">Last 14 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>

          <!-- Divider -->
          <div
            v-if="!loadingTeams"
            :class="[theme.isDark.value ? 'bg-gray-700' : 'bg-gray-300', 'w-px h-6']"
          ></div>

          <!-- Group By (shown after teams load) -->
          <div
            v-if="!loadingTeams"
            class="flex items-center space-x-2"
          >
            <label
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium whitespace-nowrap'
              ]"
            >
              Group By:
            </label>
            <select
              v-model="filters.groupBy"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="fetchLeaderboard"
            >
              <option value="team">Team</option>
              <option value="subTeam">Sub-Team</option>
            </select>
          </div>

          <!-- Divider -->
          <div
            v-if="!loadingTeams && !loadingGates"
            :class="[theme.isDark.value ? 'bg-gray-700' : 'bg-gray-300', 'w-px h-6']"
          ></div>

          <!-- Quality Gate Filter (shown after gates load) -->
          <div
            v-if="!loadingGates"
            class="flex items-center space-x-2"
          >
            <label
              :class="[
                theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                'text-xs font-medium whitespace-nowrap'
              ]"
            >
              Quality Gate:
            </label>
            <div class="relative">
              <select
                v-model="filters.qualityGate"
                :disabled="loadingGates"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                  'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer pr-8',
                  loadingGates ? 'opacity-75 cursor-wait' : ''
                ]"
                @change="fetchLeaderboard"
              >
                <option value="">All Gates</option>
                <option
                  v-for="gate in qualityGates"
                  :key="gate"
                  :value="gate"
                >
                  {{ leaderboardService.formatGateName(gate) }}
                </option>
              </select>
              <!-- Loading Spinner -->
              <div
                v-if="loadingGates"
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                <svg
                  class="animate-spin h-3 w-3 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div
        v-if="leaderboardData"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative"
      >
        <!-- Loading Overlay for Summary Cards -->
        <div
          v-if="loading"
          class="absolute inset-0 bg-gray-900 bg-opacity-30 flex items-center justify-center z-10 rounded-xl"
        >
          <svg
            class="animate-spin h-6 w-6 text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </div>

        <!-- Teams/Sub-Teams Card -->
        <div
          :class="[
            theme.isDark.value
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-gray-200',
            'rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow'
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium mb-2'
                ]"
              >
                {{ filters.groupBy === 'subTeam' ? 'Sub-Teams' : 'Teams' }}
              </p>
              <p
                :class="[
                  theme.isDark.value ? 'text-blue-400' : 'text-blue-600',
                  'text-3xl font-bold'
                ]"
              >
                {{ filteredStats.length }}
              </p>
            </div>
            <div
              :class="[
                theme.isDark.value ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50',
                'flex items-center justify-center w-10 h-10 rounded-lg'
              ]"
            >
              <svg
                :class="[theme.isDark.value ? 'text-blue-400' : 'text-blue-600', 'w-5 h-5']"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Total PRs Card -->
        <div
          :class="[
            theme.isDark.value
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-gray-200',
            'rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow'
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium mb-2'
                ]"
              >
                Total PRs
              </p>
              <p
                :class="[
                  theme.isDark.value ? 'text-purple-400' : 'text-purple-600',
                  'text-3xl font-bold'
                ]"
              >
                {{ leaderboardData.totalPRs }}
              </p>
            </div>
            <div
              :class="[
                theme.isDark.value ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50',
                'flex items-center justify-center w-10 h-10 rounded-lg'
              ]"
            >
              <svg
                :class="[theme.isDark.value ? 'text-purple-400' : 'text-purple-600', 'w-5 h-5']"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Overall Compliance Rate Card -->
        <div
          :class="[
            theme.isDark.value
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-gray-200',
            'rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow'
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium mb-2'
                ]"
              >
                Overall Compliance Rate
              </p>
              <div class="flex items-baseline space-x-2">
                <p
                  :class="[
                    leaderboardData.overallPassRate >= 100
                      ? theme.isDark.value
                        ? 'text-green-400'
                        : 'text-green-600'
                      : leaderboardData.overallPassRate >= 80
                        ? theme.isDark.value
                          ? 'text-yellow-400'
                          : 'text-yellow-600'
                        : theme.isDark.value
                          ? 'text-red-400'
                          : 'text-red-600',
                    'text-3xl font-bold'
                  ]"
                >
                  {{ leaderboardData.overallPassRate }}%
                </p>
                <span
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                    'text-sm font-medium'
                  ]"
                >
                  {{
                    leaderboardData.overallPassRate >= 100
                      ? 'Compliant'
                      : leaderboardData.overallPassRate >= 80
                        ? 'At Risk'
                        : 'Non-Compliant'
                  }}
                </span>
              </div>
              <!-- Progress bar -->
              <div
                class="mt-2.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 overflow-hidden"
              >
                <div
                  :class="[
                    leaderboardData.overallPassRate >= 100
                      ? 'bg-green-500'
                      : leaderboardData.overallPassRate >= 80
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                  ]"
                  :style="{ width: `${leaderboardData.overallPassRate}%` }"
                  class="h-full transition-all duration-500"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Failing PRs Card -->
        <div
          :class="[
            theme.isDark.value
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-gray-200',
            'rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow'
          ]"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                  'text-xs font-medium mb-2'
                ]"
              >
                Failing PRs
              </p>
              <p
                :class="[
                  theme.isDark.value ? 'text-red-400' : 'text-red-600',
                  'text-3xl font-bold'
                ]"
              >
                {{ totalFailing }}
              </p>
            </div>
            <div
              :class="[
                theme.isDark.value ? 'bg-red-900 bg-opacity-30' : 'bg-red-50',
                'flex items-center justify-center w-10 h-10 rounded-lg'
              ]"
            >
              <svg
                :class="[theme.isDark.value ? 'text-red-400' : 'text-red-600', 'w-5 h-5']"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        :class="[
          theme.isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ]"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
              Error loading compliance data
            </h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
            <button
              class="mt-2 px-3 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100 rounded text-sm"
              @click="fetchLeaderboard"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State (First Load) -->
      <div
        v-if="loading && !leaderboardData"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-xl p-12'
        ]"
      >
        <div class="flex flex-col items-center justify-center">
          <svg
            class="animate-spin h-10 w-10 text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span
            :class="[
              theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
              'mt-4 text-base font-medium'
            ]"
          >
            Loading compliance data...
          </span>
        </div>
      </div>

      <!-- Compliance Table -->
      <div
        v-else-if="leaderboardData && filteredStats.length > 0"
        class="relative"
      >
        <!-- Loading Overlay (When Refetching) -->
        <div
          v-if="loading"
          class="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-20 rounded-xl backdrop-blur-sm"
        >
          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-xl flex items-center space-x-3"
          >
            <svg
              class="animate-spin h-5 w-5 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span class="text-gray-900 dark:text-white font-medium"
              >Updating compliance data...</span
            >
          </div>
        </div>

        <!-- Table Container -->
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-xl overflow-hidden shadow-sm'
          ]"
        >
          <!-- Compact Table Header -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-900' : 'bg-gray-50',
              'px-5 py-2.5 border-b',
              theme.isDark.value ? 'border-gray-700' : 'border-gray-200'
            ]"
          >
            <div class="flex items-center justify-between">
              <h2
                :class="[
                  theme.isDark.value ? 'text-gray-200' : 'text-gray-900',
                  'text-base font-semibold'
                ]"
              >
                Compliance Overview
              </h2>
              <p :class="[theme.isDark.value ? 'text-gray-500' : 'text-gray-500', 'text-xs']">
                {{
                  filters.timeRange === '7d'
                    ? 'Last 7 days'
                    : filters.timeRange === '14d'
                      ? 'Last 14 days'
                      : 'Last 30 days'
                }}
              </p>
            </div>
          </div>

          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead :class="theme.isDark.value ? 'bg-gray-850' : 'bg-gray-100'">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                >
                  Rank
                </th>
                <th
                  scope="col"
                  class="px-5 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors select-none"
                  @click="sortBy('name')"
                >
                  <div class="flex items-center space-x-1">
                    <span>{{ filters.groupBy === 'subTeam' ? 'Sub-Team' : 'Team' }}</span>
                    <svg
                      v-if="sortState.column === 'name'"
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        v-if="sortState.order === 'asc'"
                        fill-rule="evenodd"
                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                        clip-rule="evenodd"
                      />
                      <path
                        v-else
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-5 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors select-none"
                  @click="sortBy('passRate')"
                >
                  <div class="flex items-center space-x-1">
                    <span>Compliance</span>
                    <svg
                      v-if="sortState.column === 'passRate'"
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        v-if="sortState.order === 'asc'"
                        fill-rule="evenodd"
                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                        clip-rule="evenodd"
                      />
                      <path
                        v-else
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors select-none"
                  @click="sortBy('pass')"
                >
                  <div class="flex items-center justify-center space-x-1">
                    <span>Pass</span>
                    <svg
                      v-if="sortState.column === 'pass'"
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        v-if="sortState.order === 'asc'"
                        fill-rule="evenodd"
                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                        clip-rule="evenodd"
                      />
                      <path
                        v-else
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors select-none"
                  @click="sortBy('fail')"
                >
                  <div class="flex items-center justify-center space-x-1">
                    <span>Fail</span>
                    <svg
                      v-if="sortState.column === 'fail'"
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        v-if="sortState.order === 'asc'"
                        fill-rule="evenodd"
                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                        clip-rule="evenodd"
                      />
                      <path
                        v-else
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors select-none"
                  @click="sortBy('warning')"
                >
                  <div class="flex items-center justify-center space-x-1">
                    <span>Warning</span>
                    <svg
                      v-if="sortState.column === 'warning'"
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        v-if="sortState.order === 'asc'"
                        fill-rule="evenodd"
                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                        clip-rule="evenodd"
                      />
                      <path
                        v-else
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors select-none"
                  @click="sortBy('total')"
                >
                  <div class="flex items-center justify-center space-x-1">
                    <span>Total</span>
                    <svg
                      v-if="sortState.column === 'total'"
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        v-if="sortState.order === 'asc'"
                        fill-rule="evenodd"
                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                        clip-rule="evenodd"
                      />
                      <path
                        v-else
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                >
                  Pass Rate
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="(stat, index) in filteredStats"
                :key="stat.team + '-' + (stat.subTeam || '')"
                :class="[
                  theme.isDark.value ? 'hover:bg-gray-750' : 'hover:bg-blue-50',
                  'cursor-pointer transition-all duration-150 group'
                ]"
                @click="navigateToPRList(stat)"
              >
                <!-- Rank with Medal Icons -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <span
                      v-if="index === 0"
                      class="text-2xl"
                      title="1st Place"
                    >
                      🥇
                    </span>
                    <span
                      v-else-if="index === 1"
                      class="text-2xl"
                      title="2nd Place"
                    >
                      🥈
                    </span>
                    <span
                      v-else-if="index === 2"
                      class="text-2xl"
                      title="3rd Place"
                    >
                      🥉
                    </span>
                    <span
                      v-else
                      :class="[
                        theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                        'text-sm font-semibold'
                      ]"
                    >
                      #{{ index + 1 }}
                    </span>
                  </div>
                </td>

                <!-- Team/Sub-Team Name -->
                <td class="px-5 py-4 whitespace-nowrap">
                  <div class="flex items-center space-x-2.5">
                    <div
                      :class="[
                        theme.isDark.value ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50',
                        'flex-shrink-0 h-9 w-9 rounded-lg flex items-center justify-center'
                      ]"
                    >
                      <span
                        :class="[
                          theme.isDark.value ? 'text-blue-400' : 'text-blue-600',
                          'font-bold text-xs'
                        ]"
                      >
                        {{
                          (filters.groupBy === 'subTeam'
                            ? leaderboardService.formatSubTeamName(stat.subTeam)
                            : leaderboardService.formatTeamName(stat.team)
                          )
                            .substring(0, 2)
                            .toUpperCase()
                        }}
                      </span>
                    </div>
                    <div class="flex flex-col">
                      <span
                        :class="[
                          theme.isDark.value ? 'text-white' : 'text-gray-900',
                          'font-semibold text-sm'
                        ]"
                      >
                        {{
                          filters.groupBy === 'subTeam'
                            ? leaderboardService.formatSubTeamName(stat.subTeam)
                            : leaderboardService.formatTeamName(stat.team)
                        }}
                      </span>
                      <span
                        v-if="filters.groupBy === 'subTeam' && stat.team"
                        :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-500', 'text-xs']"
                      >
                        {{ leaderboardService.formatTeamName(stat.team) }}
                      </span>
                    </div>
                  </div>
                </td>

                <!-- Visual Performance Bar -->
                <td class="px-5 py-4 whitespace-nowrap">
                  <div class="w-48">
                    <!-- Pass Rate & Feedback -->
                    <div class="flex items-center space-x-1.5 mb-1">
                      <span
                        :class="[
                          stat.passRate >= 100
                            ? theme.isDark.value
                              ? 'text-green-400'
                              : 'text-green-600'
                            : stat.passRate >= 80
                              ? theme.isDark.value
                                ? 'text-yellow-400'
                                : 'text-yellow-600'
                              : theme.isDark.value
                                ? 'text-red-400'
                                : 'text-red-600',
                          'text-base font-bold'
                        ]"
                      >
                        {{ stat.passRate }}%
                      </span>
                      <span
                        :class="[
                          theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
                          'text-xs font-medium'
                        ]"
                      >
                        {{
                          stat.passRate >= 100
                            ? 'Compliant'
                            : stat.passRate >= 80
                              ? 'At Risk'
                              : 'Non-Compliant'
                        }}
                      </span>
                    </div>
                    <!-- Single Color Progress Bar -->
                    <div
                      class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden"
                    >
                      <div
                        :class="[
                          stat.passRate >= 100
                            ? 'bg-green-500'
                            : stat.passRate >= 80
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                        ]"
                        :style="{ width: `${stat.passRate}%` }"
                        class="h-full transition-all duration-500"
                      ></div>
                    </div>
                  </div>
                </td>

                <!-- Pass Count -->
                <td class="px-4 py-4 whitespace-nowrap text-center">
                  <div class="flex items-center justify-center">
                    <span
                      :class="[
                        theme.isDark.value
                          ? 'bg-green-900 bg-opacity-30 text-green-400'
                          : 'bg-green-50 text-green-700',
                        'inline-flex items-center px-2.5 py-1 rounded-lg font-semibold text-xs'
                      ]"
                    >
                      {{ stat.pass }}
                    </span>
                  </div>
                </td>

                <!-- Fail Count -->
                <td class="px-4 py-4 whitespace-nowrap text-center">
                  <div class="flex items-center justify-center">
                    <span
                      :class="[
                        theme.isDark.value
                          ? 'bg-red-900 bg-opacity-30 text-red-400'
                          : 'bg-red-50 text-red-700',
                        'inline-flex items-center px-2.5 py-1 rounded-lg font-semibold text-xs'
                      ]"
                    >
                      {{ stat.fail }}
                    </span>
                  </div>
                </td>

                <!-- Warning Count -->
                <td class="px-4 py-4 whitespace-nowrap text-center">
                  <div class="flex items-center justify-center">
                    <span
                      :class="[
                        theme.isDark.value
                          ? 'bg-yellow-900 bg-opacity-30 text-yellow-400'
                          : 'bg-yellow-50 text-yellow-700',
                        'inline-flex items-center px-2.5 py-1 rounded-lg font-semibold text-xs'
                      ]"
                    >
                      {{ stat.warning }}
                    </span>
                  </div>
                </td>

                <!-- Total Count -->
                <td class="px-4 py-4 whitespace-nowrap text-center">
                  <span
                    :class="[
                      theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                      'font-semibold text-sm'
                    ]"
                  >
                    {{ stat.total }}
                  </span>
                </td>

                <!-- Pass Rate Badge -->
                <td class="px-4 py-4 whitespace-nowrap text-center">
                  <div class="flex flex-col items-center">
                    <span
                      :class="[
                        stat.passRate >= 100
                          ? theme.isDark.value
                            ? 'bg-green-900 bg-opacity-30 text-green-400'
                            : 'bg-green-50 text-green-700'
                          : stat.passRate >= 80
                            ? theme.isDark.value
                              ? 'bg-yellow-900 bg-opacity-30 text-yellow-400'
                              : 'bg-yellow-50 text-yellow-700'
                            : theme.isDark.value
                              ? 'bg-red-900 bg-opacity-30 text-red-400'
                              : 'bg-red-50 text-red-700',
                        'px-3 py-1.5 rounded-lg text-sm font-bold'
                      ]"
                    >
                      {{ stat.passRate }}%
                    </span>
                  </div>
                </td>

                <!-- Actions -->
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <button
                    :class="[
                      theme.isDark.value
                        ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-900 hover:bg-opacity-30'
                        : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50',
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-all inline-flex items-center'
                    ]"
                    @click.stop="navigateToPRList(stat)"
                  >
                    View Details
                    <svg
                      class="w-3.5 h-3.5 ml-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="leaderboardData && filteredStats.length === 0"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-xl p-8 text-center shadow-sm'
        ]"
      >
        <div class="flex flex-col items-center">
          <div
            class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3"
          >
            <svg
              class="h-8 w-8 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          </div>
          <h3
            :class="[
              theme.isDark.value ? 'text-white' : 'text-gray-900',
              'text-base font-semibold mb-1.5'
            ]"
          >
            No Data Available
          </h3>
          <p
            :class="[
              theme.isDark.value ? 'text-gray-400' : 'text-gray-600',
              'text-xs max-w-md mb-4'
            ]"
          >
            No quality gate results found for the selected time range and filters. Try adjusting
            your filters or check back later.
          </p>
          <button
            :class="[
              theme.isDark.value
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white',
              'px-5 py-2 rounded-lg text-xs font-medium transition-colors shadow-sm'
            ]"
            @click="fetchLeaderboard"
          >
            Refresh Data
          </button>
        </div>
      </div>

      <!-- Generated At Footer -->
      <div
        v-if="leaderboardData"
        class="text-center"
      >
        <div class="flex items-center justify-center space-x-1.5">
          <svg
            class="w-3.5 h-3.5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span :class="[theme.isDark.value ? 'text-gray-500' : 'text-gray-500', 'text-xs']">
            Generated at
            <span class="font-medium">{{ formatGeneratedAt(leaderboardData.generatedAt) }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, inject, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import { leaderboardService } from '../../services/quality-gates'
import type { LeaderboardResponse, TeamStats, TimeRange, GroupBy } from '../../types/quality-gates'
import { logger } from '../../utils/logger'

const router = useRouter()
const route = useRoute()
const theme = inject('theme', useTheme())

// State
const leaderboardData = ref<LeaderboardResponse | null>(null)
const teams = ref<string[]>([])
const qualityGates = ref<string[]>([])
const loading = ref(false)
const loadingTeams = ref(true)
const loadingGates = ref(true)
const error = ref<string | null>(null)

// Initialize filters from URL query params or defaults
const filters = reactive({
  timeRange: (route.query.timeRange as TimeRange) || '7d',
  groupBy: (route.query.groupBy as GroupBy) || 'team',
  team: (route.query.team as string) || '',
  qualityGate: (route.query.qualityGate as string) || ''
})

// Sort state
const sortState = reactive({
  column: 'passRate' as 'passRate' | 'pass' | 'fail' | 'warning' | 'total' | 'name',
  order: 'desc' as 'asc' | 'desc'
})

// Computed
const filteredStats = computed(() => {
  if (!leaderboardData.value) return []
  // Filter out teams/sub-teams with 0 total PRs
  const filtered = leaderboardData.value.stats.filter((stat) => stat.total > 0)

  // Sort based on current sort state
  return [...filtered].sort((a, b) => {
    let comparison = 0

    switch (sortState.column) {
      case 'name':
        const nameA = filters.groupBy === 'subTeam' ? a.subTeam || '' : a.team
        const nameB = filters.groupBy === 'subTeam' ? b.subTeam || '' : b.team
        comparison = nameA.localeCompare(nameB)
        break
      case 'passRate':
        comparison = a.passRate - b.passRate
        break
      case 'pass':
        comparison = a.pass - b.pass
        break
      case 'fail':
        comparison = a.fail - b.fail
        break
      case 'warning':
        comparison = a.warning - b.warning
        break
      case 'total':
        comparison = a.total - b.total
        break
    }

    return sortState.order === 'asc' ? comparison : -comparison
  })
})

const totalFailing = computed(() => {
  if (!leaderboardData.value) return 0
  return leaderboardData.value.stats.reduce((sum, stat) => sum + stat.fail, 0)
})

// Update URL with current filters
const updateURL = () => {
  const query: Record<string, string> = {
    timeRange: filters.timeRange,
    groupBy: filters.groupBy
  }

  if (filters.team) {
    query.team = filters.team
  }

  if (filters.qualityGate) {
    query.qualityGate = filters.qualityGate
  }

  // Use replace to avoid adding to browser history on every filter change
  router.replace({ query })
}

// Methods
const fetchLeaderboard = async () => {
  try {
    loading.value = true
    error.value = null

    // Update URL with current filters
    updateURL()

    logger.log('Fetching leaderboard with filters:', filters)

    const data = await leaderboardService.fetchLeaderboard({
      timeRange: filters.timeRange,
      groupBy: filters.groupBy,
      team: filters.team || undefined,
      qualityGate: filters.qualityGate || undefined
    })

    leaderboardData.value = data

    logger.log('Leaderboard loaded:', {
      teamsCount: data.stats.length,
      totalPRs: data.totalPRs
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load leaderboard'
    logger.error('Error fetching leaderboard:', err)
  } finally {
    loading.value = false
  }
}

const fetchTeams = async () => {
  try {
    loadingTeams.value = true
    const teamsData = await leaderboardService.fetchTeams()
    teams.value = teamsData.teams
  } catch (err) {
    logger.error('Error fetching teams:', err)
  } finally {
    loadingTeams.value = false
  }
}

const fetchQualityGates = async () => {
  try {
    loadingGates.value = true
    const gatesData = await leaderboardService.fetchQualityGates()
    qualityGates.value = gatesData.gates
  } catch (err) {
    logger.error('Error fetching quality gates:', err)
  } finally {
    loadingGates.value = false
  }
}

const navigateToPRList = (stat: TeamStats) => {
  // Navigate to leaderboard details page with team/subTeam filter
  const query: Record<string, string> = {
    team: stat.team,
    timeRange: filters.timeRange
  }

  if (filters.groupBy === 'subTeam' && stat.subTeam) {
    query.subTeam = stat.subTeam
  }

  if (filters.qualityGate) {
    query.qualityGate = filters.qualityGate
  }

  router.push({
    path: '/quality-gates/compliance/details',
    query
  })
}

const formatGeneratedAt = (isoString: string): string => {
  try {
    const date = new Date(isoString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  } catch {
    return isoString
  }
}

const sortBy = (column: typeof sortState.column) => {
  if (sortState.column === column) {
    // Toggle order if clicking the same column
    sortState.order = sortState.order === 'asc' ? 'desc' : 'asc'
  } else {
    // Set new column and default to descending for numbers, ascending for names
    sortState.column = column
    sortState.order = column === 'name' ? 'asc' : 'desc'
  }
}

// Watch for URL query changes (e.g., browser back/forward)
// Skip initial call since onMounted handles it
watch(
  () => route.query,
  (newQuery, oldQuery) => {
    // Only update if query actually changed (handles browser back/forward)
    const queryChanged =
      newQuery.timeRange !== oldQuery?.timeRange ||
      newQuery.groupBy !== oldQuery?.groupBy ||
      newQuery.team !== oldQuery?.team ||
      newQuery.qualityGate !== oldQuery?.qualityGate

    if (queryChanged) {
      // Update filters from URL
      filters.timeRange = (newQuery.timeRange as TimeRange) || '7d'
      filters.groupBy = (newQuery.groupBy as GroupBy) || 'team'
      filters.team = (newQuery.team as string) || ''
      filters.qualityGate = (newQuery.qualityGate as string) || ''

      // Only fetch if not already loading (prevents double-fetch)
      if (!loading.value) {
        fetchLeaderboard()
      }
    }
  }
)

// Lifecycle
onMounted(() => {
  fetchLeaderboard()
  fetchTeams()
  fetchQualityGates()
})
</script>
